// EmploymentTab.tsx
// Main container for employment section, mirrors EducationTab

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DetailData, EmploymentResponse, SkillData, useApiClient } from '@/lib/api';
import { monthInputToApiDate } from '@/lib/profile-dates';
import { Plus, X } from 'lucide-react';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import EmploymentForm from './EmploymentForm';
import EmploymentList from './EmploymentList';

// Loading skeleton component
const EmploymentLoadingSkeleton: React.FC = () => (
    <div className="space-y-6 animate-pulse">
        {[1, 2].map((i) => (
            <div key={i} className="relative border-l-2 border-gray-200 pl-4 sm:pl-6 pb-6">
                <div className="absolute w-3 h-3 bg-gray-300 rounded-full -left-[7px] top-[10px]"></div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="mt-3 space-y-2">
                    <div className="h-3 bg-gray-100 rounded w-full"></div>
                    <div className="h-3 bg-gray-100 rounded w-4/5"></div>
                </div>
                <div className="mt-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-8 bg-gray-100 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="flex flex-wrap gap-2">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-14"></div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

type StateSetter<T> = Dispatch<SetStateAction<T>>;

interface EmploymentTabProps {
  employments?: EmploymentResponse[];
  setEmployments?: StateSetter<EmploymentResponse[]>;
  details?: { [employmentId: string]: DetailData[] };
  setDetails?: StateSetter<{ [employmentId: string]: DetailData[] }>;
  skills?: { [employmentId: string]: SkillData[] };
  setSkills?: StateSetter<{ [employmentId: string]: SkillData[] }>;
};

const EmploymentTab: React.FC<EmploymentTabProps> = ({
  employments,
  setEmployments,
  details,
  setDetails,
  skills,
  setSkills,
}) => {
  const [internalEmploymentList, setInternalEmploymentList] = useState<EmploymentResponse[]>([]);
  const [internalEmploymentDetails, setInternalEmploymentDetails] = useState<{ [employmentId: string]: DetailData[] }>({});
  const [internalEmploymentSkills, setInternalEmploymentSkills] = useState<{ [employmentId: string]: SkillData[] }>({});
  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const [employment, setEmployment] = useState<EmploymentResponse>({
    title: '',
    company: '',
    start_date: '',
    end_date: '',
    is_ongoing: false,
    skills: [],
    details: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const employmentList = employments ?? internalEmploymentList;
  const employmentDetails = details ?? internalEmploymentDetails;
  const employmentSkills = skills ?? internalEmploymentSkills;

  const setEmploymentList = setEmployments ?? setInternalEmploymentList;
  const setEmploymentDetails = setDetails ?? setInternalEmploymentDetails;
  const setEmploymentSkills = setSkills ?? setInternalEmploymentSkills;

  const { api } = useApiClient();

  useEffect(() => {
    const fetchEmployments = async () => {
      try {
        setIsInitialLoading(true);
        const employments = await api.getEmployments();
        setEmploymentList(employments);

        const detailsObj: { [employmentId: string]: DetailData[] } = {};
        const skillsObj: { [employmentId: string]: SkillData[] } = {};
        await Promise.all(
          employments.map(async (emp: EmploymentResponse) => {
            if (!emp.id) return;
            try {
              const details = await api.getDetailsByEmploymentId(emp.id);
              detailsObj[emp.id] = details;
            } catch (err) {
              console.error(`[Employment] Failed to fetch details for ${emp.id}`, err);
              detailsObj[emp.id] = [];
            }
            try {
              const skills = await api.getSkillsByEmploymentId(emp.id);
              skillsObj[emp.id] = skills;
            } catch (err) {
              console.error(`[Employment] Failed to fetch skills for ${emp.id}`, err);
              skillsObj[emp.id] = [];
            }
          })
        );
        setEmploymentDetails(detailsObj);
        setEmploymentSkills(skillsObj);
      } catch (error) {
        console.error('Error fetching employments:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchEmployments();
  }, [api, setEmploymentList, setEmploymentDetails, setEmploymentSkills]);

  const createEmployment = async () => {
    if (!employment.title || !employment.company || !employment.start_date) {
      // TODO: Show error toast
      return;
    }
    setIsLoading(true);
    
    try {
      await api.createEmployment({
        title: employment.title,
        company: employment.company,
        start_date: monthInputToApiDate(employment.start_date),
        end_date: employment.is_ongoing ? undefined : monthInputToApiDate(employment.end_date) || undefined,
        is_ongoing: employment.is_ongoing,
      });
      
      // Refetch all employments to get the latest data from the server
      // The response already includes skills and details
      const employments = await api.getEmployments();
      setEmploymentList(employments);

      // Extract details and skills from the response
      const detailsObj: { [employmentId: string]: DetailData[] } = {};
      const skillsObj: { [employmentId: string]: SkillData[] } = {};
      employments.forEach((emp: EmploymentResponse) => {
        if (emp.id) {
          detailsObj[emp.id] = emp.details || [];
          skillsObj[emp.id] = emp.skills || [];
        }
      });
      setEmploymentDetails(detailsObj);
      setEmploymentSkills(skillsObj);

      setEmployment({ title: '', company: '', start_date: '', end_date: '', is_ongoing: false, skills: [], details: [] });
      setShowAddEmployment(false);
    } catch (error) {
      console.error('Error creating employment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetCreate = () => {
    setEmployment({ title: '', company: '', start_date: '', end_date: '', is_ongoing: false, skills: [], details: [] });
    setShowAddEmployment(false);
  };

  const handleEmploymentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmployment(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setEmployment(prev => ({
      ...prev,
      is_ongoing: checked,
      end_date: checked ? '' : prev.end_date,
    }));
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col sm:flex-row items-center sm:items-center justify-between space-y-2 sm:space-y-0 pb-4">
        <div className="text-center sm:text-left">
          <CardTitle className="text-lg sm:text-xl">Employment History</CardTitle>
        </div>
        <Button
          onClick={() => setShowAddEmployment(!showAddEmployment)}
          variant={showAddEmployment ? "outline" : "default"}
          size="icon"
          className={showAddEmployment ? "h-10 w-10 rounded-full sm:ml-auto" : "h-10 w-10 rounded-full bg-interview-blue text-white hover:bg-interview-blue/90 sm:ml-auto"}
          aria-label={showAddEmployment ? "Cancel adding employment" : "Add employment"}
        >
          {showAddEmployment ? (
            <X className="h-4 w-4" />
          ) : (
            <Plus className="h-5 w-5" />
          )}
        </Button>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {showAddEmployment ? (
          <EmploymentForm
            employment={employment}
            isLoading={isLoading}
            onChange={handleEmploymentChange}
            onCheckboxChange={handleCheckboxChange}
            onCreate={createEmployment}
            reset={resetCreate}
          />
        ) : (
          isInitialLoading ? (
            <EmploymentLoadingSkeleton />
          ) : (
            <EmploymentList
              employments={employmentList}
              setEmployments={setEmploymentList}
              details={employmentDetails}
              setDetails={setEmploymentDetails}
              skills={employmentSkills}
              setSkills={setEmploymentSkills}
              loading={isLoading}
              setLoading={setIsLoading}
            />
          )
        )}
      </CardContent>
    </Card>
  );
};

export default EmploymentTab;
